<template>
  <section
    :class="['owc-media-gallery-item__container', { 'owc-media-gallery-item__isEditMode': isEditMode }]"
  >
    <ValidationBoxWrapper v-if="isEditMode">
      <slot slot="validation-box" name="validation-box" />
    </ValidationBoxWrapper>
    <figure class="owc-media-gallery-item">
      <SlotContainer class="owc-media-gallery-item__children" refName="slot-media-gallery-item">
        <slot></slot>
      </SlotContainer>
      <div
        class="owc-media-gallery-item__intercept"
        @click="
          emitScreenMax();
          handleTrackingElement('image');
        "
      />
      <transition name="fade-overlay">
        <div v-show="shouldShowTileOverlay" class="owc-media-gallery-item__tile-overlay">
          <h5 class="owc-media-gallery-item__tile-overlay-title" v-html="title"></h5>
          <p
            class="owc-media-gallery-item__tile-overlay-description"
            v-if="description"
            v-html="description"
          ></p>
        </div>
      </transition>

      <figcaption
        :class="[
          'owc-media-gallery-item__caption',
          'owc-media-gallery-item__caption-gradient-mobile',
          {
            'owc-media-gallery-item__caption-gradient': !shouldShowTileOverlay,
          },
        ]"
      >
        <div v-show="!shouldShowTileOverlay" class="owc-media-gallery-item__caption-title" v-html="title" />
        <div class="owc-media-gallery-item__caption-controls">
          <!--Having aria label here with the same value is 100% redundant, but since we don't know how
            different screen readers will react to a WB web component i'll leave both-->
          <wb-tooltip-host v-if="shouldShowTileOverlay" position="end">
            <button
              type="button"
              class="owc-media-gallery-item__caption-desktop-button owc-media-gallery-item__caption-close-btn
              wb-round-button wb-round-button--theme-dark wb-round-button--small"
              :aria-label="tooltip"
              @click="shouldShowTileOverlay = !shouldShowTileOverlay"
            >
              <wb-icon class="wb-round-button__icon" aria-hidden="true" name="close"></wb-icon>
            </button>
            <!-- TODO: i18n for aria-label -->
            <wb-tooltip class="owc-media-gallery-item__controls-tooltip" aria-role="none" hidden>
              {{ tooltip }}
            </wb-tooltip>
          </wb-tooltip-host>
          <wb-tooltip-host v-else position="end">
            <button
              type="button"
              class="owc-media-gallery-item__caption-desktop-button owc-media-gallery-item__caption-open-btn
              wb-round-button wb-round-button--theme-dark wb-round-button--small"
              :aria-label="tooltip"
              @click="
                shouldShowTileOverlay = !shouldShowTileOverlay;
                handleTrackingElement('plus');
              "
            >
              <wb-icon class="wb-round-button__icon" aria-hidden="true" name="plus"></wb-icon>
            </button>
            <!-- TODO: i18n for aria-label -->
            <wb-tooltip class="owc-media-gallery-item__controls-tooltip" aria-role="none" hidden>
              {{ tooltip }}
            </wb-tooltip>
          </wb-tooltip-host>
          <button
            v-if="!shouldShowTileOverlay"
            type="button"
            aria-label="show tile modal"
            class="owc-media-gallery-item__caption-desktop-button wb-round-button wb-round-button--theme-dark wb-round-button--small"
            @click="
              emitScreenMax();
              handleTrackingElement('zoom');
            "
          >
            <wb-icon class="wb-round-button__icon" aria-hidden="true" name="screen-max"></wb-icon>
          </button>
          <button
            type="button"
            aria-label="show tile modal"
            class="owc-media-gallery-item__caption-mobile-button wb-round-button wb-round-button--theme-dark wb-round-button--small"
            @click="
              emitScreenMax();
              handleTrackingElement('zoom');
            "
          >
            <wb-icon class="wb-round-button__icon" aria-hidden="true" name="plus"></wb-icon>
          </button>
        </div>
      </figcaption>
    </figure>
  </section>
</template>

<script lang="ts">
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import {
  MEDIA_GALLERY_ITEM_ACTION_EVENT_NAME,
  MEDIA_GALLERY_ITEM_TRACKING_EVENT_NAME,
} from './mediaGallery.config';
import {
  MediaQueryService,
  WB_RESIZE_EVENT_NAME,
  mobileBreakpoints,
  Breakpoints,
} from '@owc-utils/MediaQueryService';
// Importing MediaQueryChangeEvent type declaration
import { MediaQueryChangeEvent } from '@owc-utils/MediaQueryService/MediaQueryChangeEvent';
import {
  MediaGalleryItemMethods,
  MediaGalleryItemData,
  MediaGalleryItemComputed,
  MediaGalleryItemProps,
} from '@/packages/MediaGallery/src/MediaGallery';

const wbMediaQueryService = new MediaQueryService(window);

export default ComponentInitializeDispatcher.extend<
  MediaGalleryItemData,
  MediaGalleryItemMethods,
  MediaGalleryItemComputed,
  MediaGalleryItemProps
>({
  name: 'owc-media-gallery-item',
  components: {
    SlotContainer,
    ValidationBoxWrapper,
  },
  data() {
    return {
      shouldShowTileOverlay: false,
      isMobile: true,
    };
  },
  mounted() {
    // Used to change image ratio given a MediaQuery Event
    window.addEventListener(
      WB_RESIZE_EVENT_NAME,
      this.resizeEventHandler as EventListenerOrEventListenerObject,
    );
    wbMediaQueryService.observe(true);
  },
  beforeDestroy() {
    window.removeEventListener(
      WB_RESIZE_EVENT_NAME,
      this.resizeEventHandler as EventListenerOrEventListenerObject,
    );
  },
  computed: {
    title: function() {
      return this.componentData?.title || '';
    },
    description: function() {
      return this.componentData?.description || '';
    },
    tooltip: function() {
      const showHintLabel = this.componentData.showDetailsLabel;
      const hideHintLabel = this.componentData.hideDetailsLabel;

      return this.shouldShowTileOverlay ? hideHintLabel : showHintLabel;
    },
  },
  methods: {
    emitScreenMax() {
      this.$el?.dispatchEvent(
        new CustomEvent(MEDIA_GALLERY_ITEM_ACTION_EVENT_NAME, {
          detail: {
            data: this.componentData,
            itemId: this.componentId,
          },
          composed: true,
          bubbles: true,
        }),
      );
    },
    resizeEventHandler(resizeEvent: CustomEvent<MediaQueryChangeEvent>) {
      this.isMobile = [...mobileBreakpoints, Breakpoints.mq3].includes(resizeEvent.detail.current);
    },
    handleTrackingElement(buttonType: string) {
      this.$el?.dispatchEvent(
        new CustomEvent(MEDIA_GALLERY_ITEM_TRACKING_EVENT_NAME, {
          detail: {
            buttonType,
            itemId: this.componentId,
          },
          composed: true,
          bubbles: true,
        }),
      );
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils/z-index-list';
@import '@owc-styles/utils/rte';

.owc-media-gallery-item {
  $z-index-tile: $z-index-content-lvl1;
  position: relative;

  // creating a new stacking context so it will not overlap
  // with the cookie layer
  isolation: isolate;

  /*
   * TODO: Until the store is implemented informing every tile that we are in a mobile
   * view (or using a resize handler) is really taxing. So for now a second overlay
   * is used on mobile so that the 'shouldShowTileOverlay' doesn't affect resizes.
   * The only issue right now is that if an overlay is open and the page is resized to mobile,
   * the overlay is correcly hidden, but not closed. When/if the user resizes back to desktop the overlay
   * will still be open.
   */
  @mixin background-gradient {
    background: linear-gradient(to bottom, rgba($wb-black, 0) 0%, rgba($wb-black, 1) 100%);
  }

  .owc-media-gallery-item {
    &__children {
      z-index: $z-index-negative;
    }
    &__intercept {
      position: absolute;
      top: 0;
      left: 0;

      width: 100%;
      height: 100%;
    }
    /*
   * Since we need to affect an inner child of the generic image and
   * there's no easy way to do it without needing to define new
   * behaviours on both sides, for now we opted to use a type-selector
   * until this parent <-> slotted child behaviour is defined
   * We are relying on the css custom property set by media gallery
   * to regulate the height
   */
    &__controls-tooltip {
      margin-left: var(--wb-spacing-3xs);
    }
    &__caption {
      position: absolute;
      bottom: 0px;
      left: 0px;

      z-index: $z-index-tile;

      display: flex;
      flex-direction: column;
      justify-content: flex-end;

      width: 100%;
      height: 25%;

      padding: 0 16px;
    }
    &__caption-title {
      @include wb-type-copy-strong;
      padding-right: var(--wb-spacing-xxs);
      padding-left: var(--wb-spacing-xxs);

      color: var(--wb-white);

      @include breakpoint-to(mq3) {
        display: none;
      }
    }
    &__caption-controls {
      display: flex;
      justify-content: space-between;

      margin-top: var(--wb-spacing-xxs);
      margin-bottom: var(--wb-spacing-xxs);

      @include breakpoint-to(mq3) {
        justify-content: flex-end;
      }
    }
    &__caption-desktop-button {
      display: none;

      @include breakpoint-from(mq4) {
        display: block;
      }
    }
    &__caption-mobile-button {
      @include breakpoint-from(mq4) {
        display: none;
      }
    }
    &__caption-gradient {
      @include background-gradient;
    }
    &__caption-gradient-mobile {
      @include breakpoint-to(mq3) {
        @include background-gradient;
      }
    }
    &__caption-open-btn,
    &__caption-close-btn {
      margin-right: var(--wb-spacing-3xs);
    }
    &__caption-open-btn {
      animation: animateTileOverlayIn ease-out 0.03s backwards;
    }
    &__caption-close-btn {
      transform: rotate(45deg);

      animation: animateTileOverlayOut ease-out 0.03s forwards;
    }
    &__tile-overlay {
      display: none;

      @include breakpoint-from(mq4) {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;

        display: block;

        padding: var(--wb-spacing-m) var(--wb-spacing-xs);

        color: var(--wb-white);

        background-color: rgba($wb-black, 0.5);
      }
    }
    &__tile-overlay-title {
      @include wb-type-copy-strong;

      margin-bottom: var(--wb-spacing-xs);
    }
    &__tile-overlay-description {
      @include wb-type-copy-secondary;
    }
  }
}
.fade-overlay-enter-active,
.fade-overlay-leave-active {
  transition: opacity ease-in-out 0.3s;
}
.fade-overlay-enter,
.fade-overlay-leave-to {
  opacity: 0;
}
.owc-video-player-thumbnail {
  width: 100%;
  height: var(--mediaGalleryRowHeight);
}
/*
 * The button needs an animation (instead of a transition)
 * because the button icon needs to switch from a plus icon
 * to a cross icon (which are oriented at different degrees).
 * Meaning that when the icons are switched the icons need to
 * rotated before the animation (So that a + looks similar to a x before rotating)
*/
@keyframes animateTileOverlayIn {
  from {
    transform: rotate(45deg);
  }
  to {
    transform: rotate(0deg);
  }
}
@keyframes animateTileOverlayOut {
  to {
    transform: rotate(90deg);
  }
}
</style>

<style lang="scss">
.owc-media-gallery-item__isEditMode {
  min-height: 300px;
  padding: 1.25em;

  slot {
    min-height: 150px;
  }
  .owc-media-gallery-item__caption {
    background: none;
  }
}
</style>
